import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, Suspense } from 'react';
import { JobForm } from '@/components/jobs';
import { useCreateJob } from '@/hooks/useJobs';
import type { JobFormValues } from '@/lib/schemas/job.schema';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

export const Route = createFileRoute('/admin/jobs/new')({
  component: NewJobPage,
});

function NewJobPage() {
  const navigate = useNavigate();
  const createJobMutation = useCreateJob();
  const [submitError, setSubmitError] = useState<string>();

  const handleSubmit = async (data: JobFormValues) => {
    setSubmitError(undefined);
    try {
      await createJobMutation.mutateAsync(data as any);
      // Navigate back to jobs list on success
      navigate({ to: '/admin/jobs' });
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to create job. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate({ to: '/admin/jobs' });
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={handleCancel}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Add New Job</h1>
          <p className="text-sm text-muted-foreground">
            Create a new job posting for the platform
          </p>
        </div>
      </div>

      {/* Job Form */}
      <Suspense
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        }
      >
        <JobForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={createJobMutation.isPending}
          submitError={submitError}
        />
      </Suspense>
    </div>
  );
}
